/**
 * @file feedback.ts
 * @description Strongly typed Zod schemas, constants, and validation helpers
 * for in-app user feedback, diagnostic context capture, and support ticket submissions.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

import { z } from 'zod';

/*======================================================================
 * Canonical Feedback Categories & Priorities
 *======================================================================*/

/**
 * Supported in-app feedback categories.
 */
export const FEEDBACK_CATEGORIES = ['bug', 'usability', 'feature', 'general'] as const;

/**
 * Union type representing supported feedback categories.
 */
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

/**
 * Supported feedback issue priorities.
 */
export const FEEDBACK_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;

/**
 * Union type representing supported feedback priority levels.
 */
export type FeedbackPriority = (typeof FEEDBACK_PRIORITIES)[number];

/**
 * Supported client diagnostic log levels.
 */
export const FEEDBACK_LOG_LEVELS = ['error', 'warn', 'info'] as const;

/**
 * Union type representing supported diagnostic log levels.
 */
export type FeedbackLogLevel = (typeof FEEDBACK_LOG_LEVELS)[number];

/**
 * Supported support ticket lifecycle states.
 */
export const SUPPORT_TICKET_STATUSES = ['open', 'in-review', 'resolved', 'closed'] as const;

/**
 * Union type representing supported support ticket lifecycle states.
 */
export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

/**
 * Default status assigned to newly created support tickets.
 */
export const DEFAULT_SUPPORT_TICKET_STATUS: SupportTicketStatus = 'open';

/*======================================================================
 * Configuration Defaults & Field Limits
 *======================================================================*/

/**
 * Default feedback priority assigned when omitted by submitter.
 */
export const DEFAULT_FEEDBACK_PRIORITY: FeedbackPriority = 'low';

/**
 * Default viewport device pixel ratio fallback.
 */
export const DEFAULT_VIEWPORT_PIXEL_RATIO = 1;

/**
 * Minimum character length for user feedback messages.
 */
export const MIN_FEEDBACK_MESSAGE_LENGTH = 10;

/**
 * Maximum character length for user feedback messages.
 */
export const MAX_FEEDBACK_MESSAGE_LENGTH = 2000;

/**
 * Maximum length for captured URL paths.
 */
export const MAX_FEEDBACK_URL_LENGTH = 2048;

/**
 * Maximum length for captured user-agent strings.
 */
export const MAX_FEEDBACK_USER_AGENT_LENGTH = 1024;

/**
 * Maximum length for individual console log messages.
 */
export const MAX_FEEDBACK_LOG_MESSAGE_LENGTH = 2048;

/**
 * Maximum number of recent console log entries allowed in diagnostic metadata.
 */
export const MAX_FEEDBACK_CONSOLE_LOGS_COUNT = 50;

/**
 * Maximum length for session user identifiers.
 */
export const MAX_FEEDBACK_SESSION_USER_ID_LENGTH = 256;

/*======================================================================
 * Zod Schemas
 *======================================================================*/

/**
 * Schema validating feedback categories against {@link FEEDBACK_CATEGORIES}.
 */
export const FeedbackCategorySchema = z.enum(FEEDBACK_CATEGORIES);

/**
 * Schema validating feedback priority levels against {@link FEEDBACK_PRIORITIES}.
 */
export const FeedbackPrioritySchema = z.enum(FEEDBACK_PRIORITIES);

/**
 * Schema validating client diagnostic log levels against {@link FEEDBACK_LOG_LEVELS}.
 */
export const FeedbackLogLevelSchema = z.enum(FEEDBACK_LOG_LEVELS);

/**
 * Schema validating support ticket lifecycle states against {@link SUPPORT_TICKET_STATUSES}.
 */
export const SupportTicketStatusSchema = z.enum(SUPPORT_TICKET_STATUSES);

/**
 * Schema validating a captured browser console log entry.
 */
export const ConsoleLogEntrySchema = z.object({
  level: FeedbackLogLevelSchema,
  message: z.string().max(MAX_FEEDBACK_LOG_MESSAGE_LENGTH),
  timestamp: z.number().int().positive().optional(),
});

/**
 * Type representing a captured browser console log entry.
 */
export type ConsoleLogEntry = z.infer<typeof ConsoleLogEntrySchema>;

/**
 * Schema validating client viewport dimensions.
 */
export const ViewportContextSchema = z.object({
  width: z.number().int().nonnegative(),
  height: z.number().int().nonnegative(),
  pixelRatio: z.number().positive().default(DEFAULT_VIEWPORT_PIXEL_RATIO),
});

/**
 * Type representing client viewport dimensions and resolution.
 */
export type ViewportContext = z.infer<typeof ViewportContextSchema>;

/**
 * Schema validating captured client diagnostic context metadata.
 */
export const DiagnosticContextSchema = z.object({
  url: z.string().min(1).max(MAX_FEEDBACK_URL_LENGTH),
  referrer: z.string().max(MAX_FEEDBACK_URL_LENGTH).optional(),
  viewport: ViewportContextSchema,
  userAgent: z.string().max(MAX_FEEDBACK_USER_AGENT_LENGTH),
  consoleLogs: z.array(ConsoleLogEntrySchema).max(MAX_FEEDBACK_CONSOLE_LOGS_COUNT).optional(),
  sessionUserId: z.string().max(MAX_FEEDBACK_SESSION_USER_ID_LENGTH).nullable().optional(),
});

/**
 * Type representing captured client diagnostic telemetry.
 */
export type DiagnosticContext = z.infer<typeof DiagnosticContextSchema>;

/**
 * Schema validating user feedback submissions from client surfaces.
 */
export const FeedbackSubmissionSchema = z.object({
  category: FeedbackCategorySchema,
  message: z
    .string()
    .trim()
    .min(MIN_FEEDBACK_MESSAGE_LENGTH, {
      message: `Feedback message must be at least ${MIN_FEEDBACK_MESSAGE_LENGTH} characters.`,
    })
    .max(MAX_FEEDBACK_MESSAGE_LENGTH, {
      message: `Feedback message must not exceed ${MAX_FEEDBACK_MESSAGE_LENGTH} characters.`,
    }),
  priority: FeedbackPrioritySchema.default(DEFAULT_FEEDBACK_PRIORITY),
  contactEmail: z
    .string()
    .trim()
    .email({ message: 'Invalid contact email format.' })
    .or(z.literal(''))
    .optional(),
  metadata: DiagnosticContextSchema.optional(),
  'cf-turnstile-response': z.string().optional(),
});

/**
 * Type representing a validated feedback submission payload.
 */
export type FeedbackSubmissionPayload = z.infer<typeof FeedbackSubmissionSchema>;

/**
 * Type representing raw feedback submission input before defaults are applied.
 */
export type FeedbackSubmissionInput = z.input<typeof FeedbackSubmissionSchema>;

/*======================================================================
 * Validation & Parsing Helpers
 *======================================================================*/

/**
 * Validates and parses raw feedback submission payloads.
 *
 * @param input - The raw feedback submission payload to validate.
 * @returns Clean, validated {@link FeedbackSubmissionPayload}.
 */
export function parseFeedbackSubmission(input: unknown): FeedbackSubmissionPayload {
  return FeedbackSubmissionSchema.parse(input);
}

/**
 * Validates and parses raw client diagnostic context metadata.
 *
 * @param input - The raw diagnostic metadata to validate.
 * @returns Clean, validated {@link DiagnosticContext}.
 */
export function parseDiagnosticContext(input: unknown): DiagnosticContext {
  return DiagnosticContextSchema.parse(input);
}

/*======================================================================
 * Ticket Reference Identifier Helpers
 *======================================================================*/

/**
 * Ticket ID prefix for CountTheTime customer feedback and support tickets.
 */
export const FEEDBACK_TICKET_ID_PREFIX = 'CTT-FB' as const;

/**
 * Regular expression pattern matching valid support ticket reference IDs (e.g. CTT-FB-X9A214).
 */
export const FEEDBACK_TICKET_ID_REGEX = /^CTT-FB-[A-Z0-9]{6,}$/;

/**
 * Generates a human-friendly support ticket reference ID (e.g. CTT-FB-X9A214).
 *
 * @param _nowMs - Optional legacy parameter for timestamp compatibility.
 * @returns Formatted ticket reference identifier.
 */
export function generateTicketId(_nowMs?: number): string {
  const randomHex =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '').substring(0, 6).toUpperCase()
      : Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${FEEDBACK_TICKET_ID_PREFIX}-${randomHex}`;
}

/*======================================================================
 * Operational Circuit Breaker Constants
 *======================================================================*/

/**
 * Cloudflare D1 system_control_switches key for the feedback dispatch emergency circuit breaker.
 */
export const FEEDBACK_CIRCUIT_BREAKER_SWITCH_KEY = 'alerts_feedback_paused' as const;
